// ============================================================================
//  Maintenance — department 29
// ============================================================================
//
//  ── WHY THIS DEPARTMENT, AND WHAT IT IS NOW FOR ────────────────────────────
//
//  Maintenance is department 29 in data/department.js, under Administrative
//  Departments, described there as "Facility repair, equipment upkeep". It has
//  existed in the catalogue since the beginning and has never had a module, so
//  a facility could subscribe to it and land nowhere.
//
//  It is being put to a different use: maintaining the DATA the claims side
//  depends on, starting with membership numbers. That is a deliberate
//  re-purposing rather than a mistake, and worth saying plainly here because
//  the catalogue description still reads "facility repair" — the id and the
//  name are what the permission system keys on, so they are left alone rather
//  than renamed underneath every grant that already references them.
//
//  ── ACCESS ─────────────────────────────────────────────────────────────────
//
//  Nothing here is visible until it is granted. DepartmentLayout asks
//  /getUserDepartmentComponents for this department and shows only the menu
//  keys the role has been granted; a key that is not registered in
//  `department_components` cannot be granted at all, which is why migration
//  070 registers 'MembershipManagement' before this screen can be reached.
//
//  Head Of Assembly and System Administrator see everything, as they do in
//  every other department — see FULL_ACCESS_ROLES in DepartmentLayout.
import React, { useMemo } from 'react';
import {
  Badge as MembershipIcon, Build as DepartmentIcon, Tune as SettingsIcon,
} from '@mui/icons-material';

import DepartmentLayout from '../Layout/DepartmentLayout';
import MembershipManagement from './MembershipManagement';
import MembershipSettings from './MembershipSettings';

export default function Maintenance() {
  /*  `key` is the permission name, and it is the thing that must never be
      renamed casually: component_permissions is indexed by it, so changing a
      key silently revokes access for everybody who had been granted the old
      one. The visible `label` can be changed freely. Same rule the Procurement
      module records at its own menu. */
  const menuItems = useMemo(() => [
    {
      section: 'Membership',
      key: 'MembershipManagement',
      label: 'Membership Management',
      icon: <MembershipIcon />,
      component: MembershipManagement,
    },
    /*  Kept in the same section rather than a "Configuration" one of its own:
        with two entries, a second heading is more furniture than signpost.

        Registered by migration 073 — a key with no row in department_components
        cannot be granted at all, so it would not appear in Component Access and
        an administrator would have nothing to switch on. Access to the register
        does NOT imply access to the ceiling it runs under; they are separate
        grants on purpose, because the person working the list is not
        necessarily the person who decides how fast it may run.  */
    {
      section: 'Membership',
      key: 'MembershipSettings',
      label: 'Settings',
      icon: <SettingsIcon />,
      component: MembershipSettings,
    },
  ], []);

  return (
    <DepartmentLayout
      departmentId={29}
      departmentName="Maintenance"
      departmentIcon={<DepartmentIcon />}
      menuItems={menuItems}
    />
  );
}
